/**
 * CRM Retention (win-back) worklist.
 *
 * Lists customers PortaBilling suspended/blocked for non-payment (shared pool),
 * lets an agent AI-score a customer on demand (likelihood to pay + why + how to
 * approach), log a call outcome, and view an outcome report/chart.
 */
import { escapeHtml, formatDateTimeShort, formatCurrency } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

// The Terminations tab + terminate/reverse actions are supervisor/root only.
const _canTerminate = () => hasRole('root', 'customer_care_supervisor', 'sales_supervisor');
const TERMINATION_REASON_LABELS = {
  moved_isp: 'Moved to another ISP', terminated_no_move: 'Terminated (no move)', abandoned: 'Abandoned 12mo+',
};

const API = api.routes;
const adminAPI = api;
const showFlash = (msg, type) => notify.showFlash(msg, type);

let _sort = 'recent';
let _view = 'active';
let _status = '';   // active-worklist outcome filter: '' | will_pay | not_reachable | tech_issue
let _search = '';
let _searchTimer = null;
let _offset = 0;
let _total = 0;
const _LIMIT = 50;
let _rows = [];
let _outcomeCaseId = null;
let _loadByAgent = null;

const OUTCOME_LABELS = {
  will_pay: 'Will pay', has_paid: 'Has paid', had_tech_issue: 'Tech issue',
  moved_isp: 'Moved ISP', not_reachable: 'Not reachable', reached: 'Reached',
};
const STATUS_LABELS = {
  at_risk: 'At risk', contacted: 'Contacted', will_pay: 'Will pay',
  tech_issue: 'Tech issue', recovered: 'Recovered', moved_isp: 'Moved ISP',
  abandoned: 'Abandoned', travelled: 'Travelled',
  pending_termination: 'Pending termination', terminated: 'Terminated',
};
const OUTCOME_COLORS = {
  has_paid: '#10b981', will_pay: '#0891b2', had_tech_issue: '#f59e0b',
  not_reachable: '#9ca3af', moved_isp: '#8b5cf6', reached: '#6366f1', unknown: '#d1d5db',
};
// Sub-reasons within "will pay" — shades of the will_pay teal so they read as
// parts of that segment, with a distinct legend.
const WILL_PAY_REASON_LABELS = {
  financial_constraint: 'Financial constraints', branch_access: 'Hard to reach a branch',
  no_electricity: 'No electricity', other: 'Other', unspecified: 'Unspecified',
};
const WILL_PAY_REASON_COLORS = {
  financial_constraint: '#0e7490', branch_access: '#0891b2', no_electricity: '#22b8cf',
  other: '#67d3e3', unspecified: '#a5e0ea',
};

function _fmtMoney(v) {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? formatCurrency(n) : '—';
}

function _scoreColor(s) {
  if (s == null) return '#9ca3af';
  if (s >= 67) return '#10b981';
  if (s >= 34) return '#f59e0b';
  return '#ef4444';
}

function _cooldown(lastContactAt) {
  if (!lastContactAt) return '';
  const ms = Date.now() - new Date(lastContactAt).getTime();
  if (ms < 0 || ms > 4 * 3600 * 1000) return '';
  const mins = Math.round(ms / 60000);
  const txt = mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
  return `<span title="Recently contacted — avoid double-dialling" style="margin-left:6px; font-size:11px; color:#b45309; background:#fef3c7; padding:1px 6px; border-radius:9px;">called ${escapeHtml(txt)}</span>`;
}

async function load() {
  const wrap = document.getElementById('crmRetentionList');
  if (!wrap) return;
  wrap.innerHTML = '<div class="muted">Loading…</div>';
  if (_view === 'terminations') { await loadTerminations(); return; }
  try {
    // at-risk isn't paginated; the worklist/removed views are.
    const paged = _view !== 'at_risk';
    let route;
    if (_view === 'at_risk') {
      route = API.crmRetentionAtRisk();
    } else {
      const params = { sort: _sort, view: _view, limit: String(_LIMIT), offset: String(_offset) };
      if (_search) params.search = _search;
      if (_view === 'active' && _status) params.status = _status;
      route = API.crmRetentionCandidates(new URLSearchParams(params).toString());
    }
    const data = await adminAPI.fetch(route, { method: 'GET', cache: 'no-store' });
    _rows = (data && data.items) || [];
    _total = data ? (data.total || 0) : 0;
    const countEl = document.getElementById('crmRetentionCount');
    if (countEl) {
      const noun = _view === 'removed' ? 'removed customer'
        : _view === 'at_risk' ? 'at-risk customer'
        : 'non-paying customer';
      countEl.textContent = `${_total} ${noun}${_total === 1 ? '' : 's'}`;
    }
    renderList(_total);
    renderPager(paged);
    loadTracker();   // keep the tracking sidebar in sync (fire-and-forget)
  } catch (err) {
    wrap.innerHTML = `<div class="error">Failed to load: ${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

// ---- Tracking sidebar: my lapsed commitments + all other cases ------------
function _trackRow(r, mine) {
  const scid = escapeHtml(r.support_customer_id);
  const name = escapeHtml(r.display_name || '(unnamed)');
  const phone = r.primary_phone ? `<span class="muted" style="font-size:11px;"> ${escapeHtml(r.primary_phone)}</span>` : '';
  let meta;
  let action = '';
  if (mine) {
    const when = r.follow_up_at ? formatDateTimeShort(r.follow_up_at) : '';
    meta = `<span style="font-size:11px; color:#ef4444;">promise lapsed${when ? ` · due ${escapeHtml(when)}` : ''}</span>`;
    action = `<button type="button" class="btn primary" data-track-action="outcome" data-id="${scid}" data-name="${name}" title="Log call" style="font-size:10px; padding:1px 6px;">📞</button>`;
  } else {
    const st = escapeHtml(STATUS_LABELS[r.status] || r.status);
    const tech = (r.status === 'tech_issue' && r.tech_ticket_status)
      ? ` · <span title="Tech ticket state" style="color:#f59e0b;">🛠 ${escapeHtml(String(r.tech_ticket_status).replace(/_/g, ' '))}</span>`
      : '';
    const claim = (r.claimed_by_name && !r.claimed_by_me) ? ` · <span class="muted">${escapeHtml(r.claimed_by_name)}</span>` : '';
    meta = `<span style="font-size:11px;">${st}${r.days_overdue != null ? ` · ${r.days_overdue}d` : ''}${tech}${claim}</span>`;
  }
  return `<div class="retention-track-row" data-track-goto="${scid}" title="Jump to this case" style="display:flex; align-items:center; gap:6px; padding:4px 0; border-bottom:1px dashed var(--border,#eee); cursor:pointer;">
    <div style="flex:1; min-width:0;">
      <div style="font-size:12px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;"><strong>${name}</strong>${phone}</div>
      <div>${meta}</div>
    </div>${action}
  </div>`;
}

async function loadTracker() {
  const mineEl = document.getElementById('crmRetentionMyLapsed');
  const othersEl = document.getElementById('crmRetentionOthers');
  if (!mineEl || !othersEl) return;
  try {
    const data = await adminAPI.fetch(API.crmRetentionTracker(), { method: 'GET', cache: 'no-store' });
    const mine = data.my_lapsed || [];
    const others = data.my_other || [];
    mineEl.innerHTML = mine.length
      ? mine.map((r) => _trackRow(r, true)).join('')
      : '<div class="muted" style="font-size:12px;">No lapsed commitments assigned to you. ✅</div>';
    othersEl.innerHTML = others.length
      ? others.map((r) => _trackRow(r, false)).join('')
      : '<div class="muted" style="font-size:12px;">No other cases assigned to you.</div>';
    const h = document.querySelector('#crmRetentionTracker h3');
    if (h) h.textContent = `⏰ My lapsed commitments${mine.length ? ` (${mine.length})` : ''}`;
  } catch (err) {
    mineEl.innerHTML = `<div class="error" style="font-size:12px;">${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

async function _gotoCase(scid) {
  const wrap = document.getElementById('crmRetentionList');
  if (!wrap) return;
  // Clear a previous jump-pin so only one surfaced case shows at a time.
  const oldPin = wrap.querySelector('[data-retention-pinned="1"]');
  if (oldPin) oldPin.remove();
  // Surface the case to the top: reuse its card if it's on the current page,
  // otherwise fetch it and pin a card above the list.
  let card = wrap.querySelector(`[data-retention-id="${CSS.escape(scid)}"]`);
  if (!card) {
    let c;
    try {
      c = await adminAPI.fetch(API.crmRetentionCase(scid), { method: 'GET', cache: 'no-store' });
    } catch (err) {
      showFlash(adminAPI.formatErrorDetail(err) || 'Could not load that case.', 'error');
      return;
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = _caseCardHTML(c, true).trim();
    card = tmp.firstElementChild;
  }
  // Place it just below the count line, at the top of the worklist.
  const anchor = wrap.firstChild ? wrap.firstChild.nextSibling : null;
  wrap.insertBefore(card, anchor);
  card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Open the case dossier if it isn't already expanded.
  const btn = card.querySelector('[data-retention-action="expand"]');
  if (btn && btn.getAttribute('aria-expanded') !== 'true') toggleDossier(scid, btn);
  const prev = card.style.backgroundColor;
  card.style.transition = 'background-color .4s';
  card.style.backgroundColor = 'var(--accent-soft, #fef9c3)';
  setTimeout(() => { card.style.backgroundColor = prev; }, 1200);
}

function renderPager(paged) {
  const pager = document.getElementById('crmRetentionPager');
  if (!pager) return;
  // Only show when a view paginates and there's more than one page.
  if (!paged || _total <= _LIMIT) { pager.style.display = 'none'; return; }
  pager.style.display = '';
  const from = _total ? _offset + 1 : 0;
  const to = Math.min(_offset + _LIMIT, _total);
  const info = document.getElementById('crmRetentionPageInfo');
  if (info) info.textContent = `${from}–${to} of ${_total}`;
  const prev = document.getElementById('crmRetentionPrev');
  const next = document.getElementById('crmRetentionNext');
  if (prev) prev.disabled = _offset <= 0;
  if (next) next.disabled = _offset + _LIMIT >= _total;
}

function _goToPage(deltaPages) {
  const nextOffset = _offset + deltaPages * _LIMIT;
  if (nextOffset < 0 || nextOffset >= _total) return;
  _offset = nextOffset;
  load();
}

function _removedCard(c) {
  const when = c.removed_at ? formatDateTimeShort(c.removed_at) : '';
  return `
    <div class="card" style="margin-bottom:8px; padding:10px 12px;">
      <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
        <div style="flex:1; min-width:180px;">
          <strong>${escapeHtml(c.display_name || '(unnamed)')}</strong>
          <span class="muted" style="font-size:12px;">${escapeHtml(c.primary_phone || '')}</span>
          <div class="muted" style="font-size:12px;">overdue ${_fmtMoney(c.overdue_balance)}${c.days_overdue != null ? ` · ${c.days_overdue}d` : ''}</div>
        </div>
        <div class="muted" style="flex:0 0 auto; text-align:right; font-size:12px;">${when ? `removed ${escapeHtml(when)}` : 'removed'}</div>
      </div>
      <div style="margin-top:4px; font-size:12px;"><strong>Reason:</strong> ${escapeHtml(c.removed_reason || '—')}</div>
    </div>`;
}

function _atRiskCard(c) {
  const idle = (c.idle_basis === 'pppoe' && c.idle_days != null)
    ? `<span title="Days since the last PPPoE session">💤 ${c.idle_days}d idle</span>`
    : (c.idle_basis === 'lte_unavailable'
        ? '<span class="muted" title="LTE usage lives on the MME server — not yet integrated">usage n/a (LTE)</span>'
        : '');
  const cad = c.cadence_label ? `<span class="muted">${escapeHtml(c.cadence_label)}</span>` : '';
  const susp = c.suspension_on ? ` · suspends ${escapeHtml(formatDateTimeShort(c.suspension_on))}` : '';
  const scid = escapeHtml(c.support_customer_id);
  const scoreBadge = c.ai_score != null
    ? `<span title="AI likelihood to pay/return" style="font-weight:700; color:${_scoreColor(c.ai_score)};">${c.ai_score}%</span>`
    : '';
  const claimBadge = c.claimed_by_user_id
    ? `<span title="${c.claimed_by_me ? 'You are working this case' : 'Being worked by a colleague'}" style="font-size:11px; margin-left:6px; padding:1px 7px; border-radius:9px; background:${c.claimed_by_me ? '#dcfce7' : '#e0e7ff'}; color:${c.claimed_by_me ? '#166534' : '#3730a3'};">🙋 ${escapeHtml(c.claimed_by_me ? 'Claimed by you' : `Claimed by ${c.claimed_by_name || 'a colleague'}`)}</span>`
    : '';
  let claimBtn;
  if (!c.claimed_by_user_id) {
    claimBtn = `<button type="button" class="btn ghost" data-retention-action="claim" data-id="${scid}" title="Claim this case so colleagues know you are working it">🙋 Claim</button>`;
  } else if (c.claimed_by_me) {
    claimBtn = `<button type="button" class="btn ghost" data-retention-action="release" data-id="${scid}" title="Release your claim">↩ Release</button>`;
  } else {
    claimBtn = `<button type="button" class="btn ghost" data-retention-action="claim" data-id="${scid}" title="Take this case over from ${escapeHtml(c.claimed_by_name || 'a colleague')}">🖐 Take over</button>`
      + `<button type="button" class="btn ghost" data-retention-action="release" data-id="${scid}" title="Supervisors can release a colleague's claim">↩ Release</button>`;
  }
  const hint = c.ai_reason
    ? `<div class="muted" style="font-size:12px; margin-top:4px;"><strong>Why:</strong> ${escapeHtml(c.ai_reason)}${c.ai_suggested_approach ? ` · <strong>Approach:</strong> ${escapeHtml(c.ai_suggested_approach)}` : ''}</div>`
    : '';
  return `
    <div class="card" data-retention-id="${scid}" style="margin-bottom:8px; padding:10px 12px;">
      <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
        <div style="flex:1; min-width:180px;">
          <strong>${escapeHtml(c.display_name || '(unnamed)')}</strong>
          <span class="muted" style="font-size:12px;">${escapeHtml(c.primary_phone || '')}</span>${claimBadge}
          <div class="muted" style="font-size:12px;">overdue ${_fmtMoney(c.overdue_balance)}${c.days_overdue != null ? ` · ${c.days_overdue}d` : ''}${susp}</div>
        </div>
        <div style="flex:0 0 auto; font-size:12px; display:flex; gap:12px; align-items:center;">${scoreBadge}${cad}${idle}</div>
        <div style="flex:0 0 auto; display:flex; gap:6px;">
          ${claimBtn}
          <button type="button" class="btn ghost" data-retention-action="expand" data-id="${scid}" aria-expanded="false" title="Show contact, ticket and payment history">▸ Details</button>
          <button type="button" class="btn ghost" data-retention-action="score" data-id="${scid}">${c.ai_score != null ? 'Re-score' : '✨ Score'}</button>
          <button type="button" class="btn primary" data-retention-action="outcome" data-id="${scid}" data-name="${escapeHtml(c.display_name || '')}">📞 Log call</button>
        </div>
      </div>
      ${hint}
      <div class="retention-dossier" data-retention-dossier="${scid}" style="display:none; margin-top:8px; border-top:1px solid var(--border, #eee); padding-top:8px;"></div>
    </div>`;
}

function renderList(total) {
  const wrap = document.getElementById('crmRetentionList');
  if (!wrap) return;
  if (!_rows.length) {
    let empty;
    if (_search) {
      empty = `No customers match “${escapeHtml(_search)}” in this view.`;
    } else {
      empty = _view === 'removed' ? 'No customers have been removed from the worklist.'
        : _view === 'at_risk' ? 'No active customers are approaching suspension. ✅'
        : 'No suspended/blocked non-payers right now. ✅';
    }
    wrap.innerHTML = `<div class="muted">${empty}</div>`;
    return;
  }
  if (_view === 'removed' || _view === 'at_risk') {
    const cardFn = _view === 'removed' ? _removedCard : _atRiskCard;
    wrap.innerHTML = `<div class="muted" style="margin-bottom:8px;">${_rows.length} shown${total && total > _rows.length ? ` of ${total}` : ''}</div>${_rows.map(cardFn).join('')}`;
    return;
  }
  const cards = _rows.map(_caseCardHTML).join('');
  const countLine = `<div class="muted" style="margin-bottom:8px;">${_rows.length} shown${total && total > _rows.length ? ` of ${total}` : ''}</div>`;
  wrap.innerHTML = countLine + cards;
}

// Full worklist case card. `pinned` marks a case surfaced from the tracker that
// lives on another page of the worklist, so the agent can see it was jumped to.
function _caseCardHTML(c, pinned = false) {
  const scoreBadge = c.ai_score != null
    ? `<span title="AI likelihood to pay/return" style="font-weight:700; color:${_scoreColor(c.ai_score)};">${c.ai_score}%</span>`
    : '<span class="muted">not scored</span>';
  const hint = c.ai_reason
    ? `<div class="muted" style="font-size:12px; margin-top:2px;"><strong>Why:</strong> ${escapeHtml(c.ai_reason)}${c.ai_suggested_approach ? ` · <strong>Approach:</strong> ${escapeHtml(c.ai_suggested_approach)}` : ''}</div>`
    : '';
  const last = c.last_outcome
    ? `<span style="font-size:12px;">Last: ${escapeHtml(OUTCOME_LABELS[c.last_outcome] || c.last_outcome)}${c.last_contact_at ? ` · ${escapeHtml(formatDateTimeShort(c.last_contact_at))}` : ''}</span>${_cooldown(c.last_contact_at)}`
    : '<span class="muted" style="font-size:12px;">not contacted</span>';
  const followUp = c.follow_up_at ? `<span class="muted" style="font-size:12px; margin-left:8px;">↻ follow-up ${escapeHtml(formatDateTimeShort(c.follow_up_at))}</span>` : '';
  const lapsed = c.commitment_lapsed ? '<span style="font-size:11px; margin-left:8px; padding:1px 6px; border-radius:8px; background:#fee2e2; color:#991b1b;">⚠ commitment lapsed</span>' : '';
  const claimBadge = c.claimed_by_user_id
    ? `<span title="${c.claimed_by_me ? 'You are working this case' : 'Being worked by a colleague'}" style="font-size:11px; margin-left:6px; padding:1px 7px; border-radius:9px; background:${c.claimed_by_me ? '#dcfce7' : '#e0e7ff'}; color:${c.claimed_by_me ? '#166534' : '#3730a3'};">🙋 ${escapeHtml(c.claimed_by_me ? 'Claimed by you' : `Claimed by ${c.claimed_by_name || 'a colleague'}`)}</span>`
    : '';
  let claimBtn;
  if (!c.claimed_by_user_id) {
    claimBtn = `<button type="button" class="btn ghost" data-retention-action="claim" data-id="${escapeHtml(c.support_customer_id)}" title="Claim this case so colleagues know you are working it">🙋 Claim</button>`;
  } else if (c.claimed_by_me) {
    claimBtn = `<button type="button" class="btn ghost" data-retention-action="release" data-id="${escapeHtml(c.support_customer_id)}" title="Release your claim">↩ Release</button>`;
  } else {
    // Claimed by a colleague → allow a takeover (reassigns to you); supervisors can also release.
    claimBtn = `<button type="button" class="btn ghost" data-retention-action="claim" data-id="${escapeHtml(c.support_customer_id)}" title="Take this case over from ${escapeHtml(c.claimed_by_name || 'a colleague')}">🖐 Take over</button>`
      + `<button type="button" class="btn ghost" data-retention-action="release" data-id="${escapeHtml(c.support_customer_id)}" title="Supervisors can release a colleague's claim">↩ Release</button>`;
  }
  const pinNote = pinned
    ? '<div class="muted" style="font-size:11px; margin-bottom:6px;">📌 Jumped from your tracker — this case is on another page of the worklist.</div>'
    : '';
  const pinStyle = pinned ? ' border:1px solid var(--accent, #6366f1); box-shadow:0 1px 6px rgba(99,102,241,.25);' : '';
  const pinAttr = pinned ? ' data-retention-pinned="1"' : '';
  return `
      <div class="card" data-retention-id="${escapeHtml(c.support_customer_id)}"${pinAttr} style="margin-bottom:8px; padding:10px 12px;${pinStyle}">
        ${pinNote}
        <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
          <div style="flex:1; min-width:180px;">
            <strong>${escapeHtml(c.display_name || '(unnamed)')}</strong>
            <span class="muted" style="font-size:12px;">${escapeHtml(c.primary_phone || '')}</span>${claimBadge}
            <div class="muted" style="font-size:12px;">${escapeHtml((c.account_state || '').toUpperCase())} · overdue ${_fmtMoney(c.overdue_balance)}${c.days_overdue != null ? ` · ${c.days_overdue}d` : ''}${c.mrr != null ? ` · MRR ${_fmtMoney(c.mrr)}` : ''} · ${escapeHtml(STATUS_LABELS[c.status] || c.status)}</div>
          </div>
          <div style="flex:0 0 auto; text-align:right;">${scoreBadge}</div>
          <div style="flex:0 0 auto; display:flex; gap:6px;">
            ${claimBtn}
            <button type="button" class="btn ghost" data-retention-action="expand" data-id="${escapeHtml(c.support_customer_id)}" aria-expanded="false" title="Show contact, ticket and payment history">▸ Details</button>
            <button type="button" class="btn ghost" data-retention-action="score" data-id="${escapeHtml(c.support_customer_id)}">${c.ai_score != null ? 'Re-score' : '✨ Score'}</button>
            <button type="button" class="btn primary" data-retention-action="outcome" data-id="${escapeHtml(c.support_customer_id)}" data-name="${escapeHtml(c.display_name || '')}">📞 Log call</button>
            <button type="button" class="btn ghost" data-retention-action="remove" data-id="${escapeHtml(c.support_customer_id)}" data-name="${escapeHtml(c.display_name || '')}" title="Remove this customer from the retention worklist">🗑 Remove</button>
          </div>
        </div>
        ${hint}
        <div style="margin-top:4px;">${last}${followUp}${lapsed}</div>
        <div class="retention-dossier" data-retention-dossier="${escapeHtml(c.support_customer_id)}" style="display:none; margin-top:8px; border-top:1px solid var(--border, #eee); padding-top:8px;"></div>
      </div>`;
}

// ---- Expandable case dossier (contact / ticket / payment history) -----------
const _dossierCache = {};

async function toggleDossier(id, btn) {
  const box = document.querySelector(`.retention-dossier[data-retention-dossier="${CSS.escape(id)}"]`);
  if (!box) return;
  const open = box.style.display !== 'none';
  if (open) {
    box.style.display = 'none';
    if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.textContent = '▸ Details'; }
    return;
  }
  box.style.display = '';
  if (btn) { btn.setAttribute('aria-expanded', 'true'); btn.textContent = '▾ Details'; }
  if (_dossierCache[id]) { box.innerHTML = renderDossier(_dossierCache[id]); return; }
  box.innerHTML = '<div class="muted" style="font-size:12px;">Loading history…</div>';
  try {
    const data = await adminAPI.fetch(API.crmRetentionDossier(id), { method: 'GET' });
    _dossierCache[id] = data;
    box.innerHTML = renderDossier(data);
    // Lazy 12-month backfill on first expand if the deep history isn't loaded.
    if (data.payment && data.payment.cadence && !data.payment.cadence.complete) backfillCadence(id);
  } catch (err) {
    box.innerHTML = `<div class="error" style="font-size:12px;">Failed to load: ${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

function renderDossier(d) {
  // Two responsive columns: payments + contact history on the left, ticket
  // history on the right when there's horizontal room. `flex-wrap` collapses
  // them back to a single stacked column on narrow dialogs.
  return `<div style="display:flex; flex-wrap:wrap; gap:14px; align-items:flex-start;">
    <div style="flex:1 1 320px; min-width:280px; display:grid; gap:10px;">
      ${_dossierPayment(d.payment)}
      ${_dossierCalls(d.calls || [])}
    </div>
    <div style="flex:1 1 280px; min-width:260px;">
      ${_dossierTickets(d.tickets || [])}
    </div>
  </div>`;
}

function _dossierSection(title, inner) {
  return `<div><div style="font-weight:600; font-size:12px; margin-bottom:4px;">${title}</div>${inner}</div>`;
}

function _dossierCalls(calls) {
  if (!calls.length) return _dossierSection('📞 Contact history', '<div class="muted" style="font-size:12px;">No contacts logged.</div>');
  const rows = calls.map((c) => {
    const who = c.actor_name
      ? ` · <span title="Logged by">by <strong>${escapeHtml(c.actor_name)}</strong></span>`
      : '';
    const oc = c.outcome ? ` · <strong>${escapeHtml(OUTCOME_LABELS[c.outcome] || c.outcome)}</strong>` : '';
    const ch = c.channel ? ` (${escapeHtml(c.channel)})` : '';
    const body = c.body ? `<div class="muted" style="font-size:12px;">${escapeHtml(c.body)}</div>` : '';
    return `<div style="padding:4px 0; border-bottom:1px dashed var(--border, #eee);">
      <span style="font-size:12px;">${escapeHtml(formatDateTimeShort(c.at))}${oc}${ch}${who}</span>${body}</div>`;
  }).join('');
  return _dossierSection('📞 Contact history', rows);
}

function _dossierTickets(tickets) {
  if (!tickets.length) return _dossierSection('🎫 Ticket history', '<div class="muted" style="font-size:12px;">No tickets.</div>');
  const rows = tickets.map((t) => {
    const st = t.status ? `<span style="font-size:11px; padding:1px 6px; border-radius:8px; background:var(--chip, #eef);">${escapeHtml(String(t.status).replace(/_/g, ' '))}</span>` : '';
    const summary = t.resolution_summary ? ` · <span class="muted">${escapeHtml(t.resolution_summary)}</span>` : '';
    return `<div style="padding:4px 0; border-bottom:1px dashed var(--border, #eee);">
      <span style="font-size:12px;">${escapeHtml(formatDateTimeShort(t.created_at))} · ${escapeHtml(t.subject || 'Ticket')}</span> ${st}<span style="font-size:12px;">${summary}</span></div>`;
  }).join('');
  return _dossierSection('🎫 Ticket history', rows);
}

function _dossierPayment(p) {
  if (!p) return _dossierSection('💳 Payments', '<div class="muted" style="font-size:12px;">No billing snapshot.</div>');
  const facts = [
    p.last_payment_at ? `Last paid ${escapeHtml(formatDateTimeShort(p.last_payment_at))}` : 'No recorded payment',
    p.payments_31d_count != null ? `${p.payments_31d_count} payment(s)/31d (${_fmtMoney(p.payments_31d_total)})` : null,
    p.next_due_date ? `Next due ${escapeHtml(formatDateTimeShort(p.next_due_date))}` : null,
    p.overdue_balance != null ? `Overdue ${_fmtMoney(p.overdue_balance)}` : null,
  ].filter(Boolean).join(' · ');
  const recent = (p.recent || []).length
    ? `<div style="display:flex; gap:6px; flex-wrap:wrap; margin-top:4px;">${p.recent.map((r) =>
        `<span style="font-size:11px; padding:1px 6px; border-radius:8px; background:var(--chip, #eef);" title="${escapeHtml(r.at ? formatDateTimeShort(r.at) : '')}">${_fmtMoney(r.amount)}</span>`).join('')}</div>`
    : '';
  return _dossierSection('💳 Payments', `<div class="muted" style="font-size:12px;">${facts}</div>${recent}${_cadenceStrip(p.cadence)}`);
}

// 12-month paid/skipped strip. Present month with paid>0 = paid (green); absent
// month = no payment — red once the deep history is backfilled (complete), grey
// while still unknown.
function _cadenceStrip(cadence) {
  if (!cadence) return '';
  const map = {};
  (cadence.months || []).forEach((m) => { map[m.month] = m; });
  const now = new Date();
  const cells = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const m = map[key];
    const paid = !!m && m.paid > 0;
    const color = paid ? '#10b981' : (cadence.complete ? '#ef4444' : '#d1d5db');
    const title = `${key}: ${m ? _fmtMoney(m.paid) : (cadence.complete ? 'no payment' : 'unknown')}`;
    cells.push(`<span title="${escapeHtml(title)}" style="display:inline-block; width:10px; height:14px; border-radius:2px; background:${color};"></span>`);
  }
  const note = cadence.complete ? '' : ' <span class="muted" style="font-size:11px;">· loading 12-month history…</span>';
  const cls = cadence.classification;
  const clsLabel = cls && cls.label
    ? ` · <strong title="${escapeHtml(cls.detail || '')}">${escapeHtml(cls.label)}</strong>`
    : '';
  return `<div style="margin-top:6px;"><div class="muted" style="font-size:11px; margin-bottom:2px;">Payment cycle (12 mo)${clsLabel}${note}</div><div style="display:flex; gap:2px;">${cells.join('')}</div></div>`;
}

async function backfillCadence(id) {
  try {
    const cadence = await adminAPI.fetch(API.crmRetentionCadence(id), { method: 'POST' });
    const cached = _dossierCache[id];
    if (cached && cached.payment) cached.payment.cadence = cadence;
    const box = document.querySelector(`.retention-dossier[data-retention-dossier="${CSS.escape(id)}"]`);
    if (box && box.style.display !== 'none' && cached) box.innerHTML = renderDossier(cached);
  } catch (err) {
    // Leave the partial strip in place; nothing else to do.
  }
}

async function removeCase(id, name) {
  const reason = window.prompt(`Remove ${name || 'this customer'} from the retention worklist.\n\nReason (required):`, '');
  if (reason == null) return;                 // cancelled
  const trimmed = reason.trim();
  if (!trimmed) { showFlash('A reason is required to remove a customer.', 'error'); return; }
  try {
    await adminAPI.fetch(API.crmRetentionRemove(id), { method: 'POST', body: JSON.stringify({ reason: trimmed }) });
    showFlash('Customer removed from the retention worklist.');
    load();
  } catch (err) {
    showFlash(adminAPI.formatErrorDetail(err) || 'Failed to remove customer', 'error');
  }
}

async function scoreCase(id) {
  const card = document.querySelector(`[data-retention-id="${CSS.escape(id)}"]`);
  const btn = card && card.querySelector('[data-retention-action="score"]');
  if (btn) { btn.disabled = true; btn.textContent = 'Scoring…'; }
  try {
    const updated = await adminAPI.fetch(API.crmRetentionScore(id), { method: 'POST' });
    // At-risk rows differ in shape from a case response — reload rather than splice.
    if (_view === 'at_risk') return void load();
    const i = _rows.findIndex((r) => String(r.support_customer_id) === String(id));
    if (i >= 0) _rows[i] = updated;
    renderList(_rows.length);
  } catch (err) {
    showFlash(`Scoring failed: ${adminAPI.formatErrorDetail(err)}`, 'error');
    if (btn) { btn.disabled = false; btn.textContent = '✨ Score'; }
  }
}

async function claimCase(id, release) {
  const card = document.querySelector(`[data-retention-id="${CSS.escape(id)}"]`);
  const btn = card && card.querySelector(`[data-retention-action="${release ? 'release' : 'claim'}"]`);
  if (btn) btn.disabled = true;
  try {
    const route = release ? API.crmRetentionRelease(id) : API.crmRetentionClaim(id);
    const updated = await adminAPI.fetch(route, { method: 'POST' });
    // The at-risk row has a different shape than a case response — reload it
    // rather than splice the case object in.
    if (_view === 'at_risk') return void load();
    const i = _rows.findIndex((r) => String(r.support_customer_id) === String(id));
    if (i >= 0) _rows[i] = updated;
    renderList(_rows.length);
  } catch (err) {
    if (btn) btn.disabled = false;
    showFlash(adminAPI.formatErrorDetail(err) || (release ? 'Could not release' : 'Could not claim'), 'error');
  }
}

// ---- Terminations tab (supervisor/root) ------------------------------------
async function loadTerminations() {
  const wrap = document.getElementById('crmRetentionList');
  if (!wrap) return;
  const pager = document.getElementById('crmRetentionPager');
  if (pager) pager.style.display = 'none';  // not paginated
  const countEl = document.getElementById('crmRetentionCount');
  try {
    const data = await adminAPI.fetch(API.crmRetentionTerminations(), { method: 'GET', cache: 'no-store' });
    const items = (data && data.items) || [];
    if (countEl) countEl.textContent = `${items.length} pending termination${items.length === 1 ? '' : 's'}`;
    renderTerminations(items);
  } catch (err) {
    wrap.innerHTML = `<div class="error">Failed to load: ${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

function renderTerminations(items) {
  const wrap = document.getElementById('crmRetentionList');
  if (!wrap) return;
  if (!items.length) {
    wrap.innerHTML = '<div class="muted">No customers are pending termination. ✅</div>';
    return;
  }
  const intro = '<div class="muted" style="margin-bottom:8px;">Confirming executes an <strong>irreversible</strong> PortaBilling termination (notification suppressed). One at a time — no bulk. Reverse returns a case to the active worklist.</div>';
  wrap.innerHTML = intro + items.map(_terminationCard).join('');
}

function _terminationCard(t) {
  const reason = TERMINATION_REASON_LABELS[t.termination_reason] || t.termination_reason || '—';
  const marked = t.marked_by_name ? ` · marked by ${escapeHtml(t.marked_by_name)}` : '';
  const pending = t.days_pending != null ? ` · pending ${t.days_pending}d` : '';
  const err = t.termination_error
    ? `<div style="font-size:11px; margin-top:4px; padding:3px 7px; border-radius:6px; background:#fee2e2; color:#991b1b;">⚠ Last attempt failed: ${escapeHtml(t.termination_error)} — retry below.</div>`
    : '';
  return `<div class="card" data-termination-id="${escapeHtml(t.support_customer_id)}" style="margin-bottom:8px; padding:10px 12px;">
      <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
        <div style="flex:1; min-width:180px;">
          <strong>${escapeHtml(t.display_name || '(unnamed)')}</strong>
          <span class="muted" style="font-size:12px;">${escapeHtml(t.primary_phone || '')}</span>
          <div class="muted" style="font-size:12px;">${escapeHtml(reason)}${marked}${pending}</div>
        </div>
        <div style="flex:0 0 auto; display:flex; gap:6px;">
          <button type="button" class="btn ghost" data-termination-action="reverse" data-id="${escapeHtml(t.support_customer_id)}" data-name="${escapeHtml(t.display_name || '')}" title="Return this case to the active worklist">↩ Reverse</button>
          <button type="button" class="btn primary" data-termination-action="terminate" data-id="${escapeHtml(t.support_customer_id)}" data-name="${escapeHtml(t.display_name || '')}" title="Execute the irreversible PortaBilling termination" style="background:#b91c1c;">⛔ ${t.termination_error ? 'Retry terminate' : 'Confirm termination'}</button>
        </div>
      </div>${err}
    </div>`;
}

async function terminateCase(id, name) {
  if (!window.confirm(`Terminate ${name || 'this customer'} in PortaBilling?\n\nThis is IRREVERSIBLE. The account will be terminated (notification suppressed). Continue?`)) return;
  const card = document.querySelector(`[data-termination-id="${CSS.escape(id)}"]`);
  const btn = card && card.querySelector('[data-termination-action="terminate"]');
  if (btn) { btn.disabled = true; btn.textContent = 'Terminating…'; }
  try {
    await adminAPI.fetch(API.crmRetentionTerminate(id), { method: 'POST' });
    showFlash('Customer terminated in PortaBilling.', 'success');
    await loadTerminations();
  } catch (err) {
    if (btn) { btn.disabled = false; btn.textContent = '⛔ Retry terminate'; }
    showFlash(`Termination failed: ${adminAPI.formatErrorDetail(err)}. The case stays pending for retry.`, 'error');
    await loadTerminations();
  }
}

async function reverseTermination(id, name) {
  if (!window.confirm(`Reverse the pending termination for ${name || 'this customer'} and return it to the active worklist?`)) return;
  try {
    await adminAPI.fetch(API.crmRetentionReverseTermination(id), { method: 'POST' });
    showFlash('Returned to the active worklist.', 'success');
    await loadTerminations();
  } catch (err) {
    showFlash(`Could not reverse: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

// The "will pay" reason sub-select is only relevant when the outcome is will_pay.
function _syncOutcomeReasonVisibility() {
  const sel = document.getElementById('crmRetentionOutcomeSelect');
  const field = document.getElementById('crmRetentionReasonField');
  if (field) field.style.display = (sel && sel.value === 'will_pay') ? '' : 'none';
}

function openOutcome(id, name) {
  _outcomeCaseId = id;
  const dlg = document.getElementById('crmRetentionOutcomeDlg');
  if (!dlg) return;
  const title = document.getElementById('crmRetentionOutcomeTitle');
  if (title) title.textContent = `Log call — ${name || 'customer'}`;
  const note = document.getElementById('crmRetentionNote');
  if (note) note.value = '';
  const fu = document.getElementById('crmRetentionFollowUp');
  if (fu) fu.value = '';
  const sel = document.getElementById('crmRetentionOutcomeSelect');
  if (sel) sel.value = 'will_pay';
  const reason = document.getElementById('crmRetentionReasonSelect');
  if (reason) reason.value = '';
  _syncOutcomeReasonVisibility();
  openDialog(dlg);
}

async function saveOutcome() {
  if (!_outcomeCaseId) return;
  const sel = document.getElementById('crmRetentionOutcomeSelect');
  const note = document.getElementById('crmRetentionNote');
  const fu = document.getElementById('crmRetentionFollowUp');
  const reason = document.getElementById('crmRetentionReasonSelect');
  const payload = { outcome: sel ? sel.value : 'will_pay' };
  if (payload.outcome === 'will_pay' && reason && reason.value) payload.reason = reason.value;
  if (note && note.value.trim()) payload.note = note.value.trim();
  if (fu && fu.value) payload.follow_up_at = new Date(fu.value + 'T00:00:00').toISOString();
  try {
    await adminAPI.fetch(API.crmRetentionOutcome(_outcomeCaseId), { method: 'POST', body: JSON.stringify(payload) });
    showFlash('Outcome logged', 'success');
    const dlg = document.getElementById('crmRetentionOutcomeDlg');
    if (dlg) closeDialog(dlg);
    await load();
    if (_reportOpen()) loadReport();   // refresh the report if it's expanded
  } catch (err) {
    showFlash(`Could not save: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

function renderReport(rep) {
  const box = document.getElementById('crmRetentionReport');
  if (!box) return;
  const outcomes = rep.outcomes || {};
  const reasons = rep.will_pay_reasons || {};
  const hasReasons = Object.values(reasons).reduce((a, b) => a + b, 0) > 0;
  const total = Object.values(outcomes).reduce((a, b) => a + b, 0);
  let bar = '<div class="muted" style="font-size:12px;">No engagements in the period yet.</div>';
  let legend = '';
  if (total) {
    const segs = Object.entries(outcomes).map(([k, n]) => {
      const pct = (n / total) * 100;
      // Subdivide the "will pay" segment into its sub-reasons (shades of teal).
      if (k === 'will_pay' && hasReasons && n > 0) {
        const sub = Object.entries(reasons).map(([rk, rn]) => {
          const rpct = (rn / n) * 100;
          const rc = WILL_PAY_REASON_COLORS[rk] || WILL_PAY_REASON_COLORS.unspecified;
          return `<div title="Will pay · ${escapeHtml(WILL_PAY_REASON_LABELS[rk] || rk)}: ${rn}" style="width:${rpct}%; background:${rc};"></div>`;
        }).join('');
        return `<div style="width:${pct}%; display:flex;">${sub}</div>`;
      }
      const color = OUTCOME_COLORS[k] || OUTCOME_COLORS.unknown;
      return `<div title="${escapeHtml(OUTCOME_LABELS[k] || k)}: ${n}" style="width:${pct}%; background:${color};"></div>`;
    }).join('');
    bar = `<div style="display:flex; height:22px; border-radius:8px; overflow:hidden; background:var(--panel-2,#f1f5f9);">${segs}</div>`;
    legend = '<div style="display:flex; gap:12px; flex-wrap:wrap; margin-top:6px; font-size:12px;">' +
      Object.entries(outcomes).map(([k, n]) => {
        const color = OUTCOME_COLORS[k] || OUTCOME_COLORS.unknown;
        return `<span><span style="display:inline-block; width:10px; height:10px; border-radius:2px; background:${color}; margin-right:4px;"></span>${escapeHtml(OUTCOME_LABELS[k] || k)} (${n})</span>`;
      }).join('') + '</div>';
    if (hasReasons) {
      legend += '<div style="margin-top:4px; font-size:11px; color:var(--sub);">Will-pay reasons: ' +
        Object.entries(reasons).map(([rk, rn]) => {
          const rc = WILL_PAY_REASON_COLORS[rk] || WILL_PAY_REASON_COLORS.unspecified;
          return `<span style="margin-right:10px;"><span style="display:inline-block; width:9px; height:9px; border-radius:2px; background:${rc}; margin-right:3px;"></span>${escapeHtml(WILL_PAY_REASON_LABELS[rk] || rk)} (${rn})</span>`;
        }).join('') + '</div>';
    }
  }
  const rec = rep.recovery || {};
  const rev = rep.revenue || {};

  // Funnel — where customers fall out.
  const sc = rep.status_counts || {};
  const order = ['at_risk', 'contacted', 'will_pay', 'tech_issue', 'recovered', 'moved_isp', 'abandoned', 'travelled', 'removed'];
  const funnelItems = order.filter((s) => sc[s]).map((s) =>
    `<span><strong>${sc[s]}</strong> <span class="muted">${escapeHtml(STATUS_LABELS[s] || s)}</span></span>`).join('');

  const bd = rep.breakdowns || {};

  // KPI cards (fill horizontal space via the dashboard-metrics auto-fit grid).
  // A `segment` makes the card a clickable drill-down (view + export those
  // customers) — mirrors the CRM customer report tiles.
  const card = (label, value, color, sub, segment) => {
    const attrs = segment
      ? ` data-segment="${escapeHtml(segment)}" role="button" tabindex="0" title="Click to view & export these customers" style="cursor:pointer;"`
      : '';
    return `<div class="pending-dashboard-card"${attrs}><span class="label">${escapeHtml(label)}${segment ? ' 🔎' : ''}</span>`
      + `<span class="value"${color ? ` style="color:${color};"` : ''}>${value}</span>`
      + `${sub ? `<span class="sub">${sub}</span>` : ''}</div>`;
  };
  const metrics = `<div class="pending-dashboard-metrics">
    ${card(`Contacted (${rep.days}d)`, rep.contacted, null, null, 'contacted')}
    ${card('Recovered', rep.recovered, '#10b981', 'agent confirmed (paid)', 'recovered')}
    ${card('Auto-recovered', rep.auto_recovered || 0, '#0891b2', 'self-resumed paying', 'auto_recovered')}
    ${card('Recovery rate', `${rep.recovery_rate}%`)}
    ${card('Durable', rec.durable || 0, '#10b981', 'still active', 'durable')}
    ${card('Re-suspended', rec.resuspended || 0, '#ef4444', 'revolving door', 'resuspended')}
    ${card('Still ticketing', rec.ticketing || 0, '#f59e0b', null, 'ticketing')}
    ${card('Durability rate', `${rec.durability_rate || 0}%`)}
    ${card('MRR saved', _fmtMoney(rev.mrr_recovered), '#10b981', 'recovered', 'recovered')}
    ${card('MRR lost', _fmtMoney(rev.mrr_lost), '#8b5cf6', 'moved ISP', 'moved_isp')}
    ${card('Still active', rep.active, null, null, 'active')}
  </div>`;

  // Panels (tile across horizontal space via the dashboard-panels auto-fit grid).
  const panel = (title, inner) => `<div class="pending-dashboard-panel"><h3>${title}</h3><div>${inner}</div></div>`;
  const funnelPanel = funnelItems
    ? panel('Funnel', `<div style="display:flex; gap:14px; flex-wrap:wrap; font-size:12px;">${funnelItems}</div>`)
    : '';
  // These lists (esp. "By plan") can run to dozens of rows — cap the height and
  // scroll so a tile never takes over the screen.
  const breakdownPanels = ['class', 'region', 'plan']
    .filter((d) => bd[d] && Object.keys(bd[d]).length)
    .map((d) => panel(`By ${d} · total · recovered`,
      `<div class="crm-breakdown-scroll">${_breakdownRows(bd[d])}</div>`))
    .join('');

  box.innerHTML = `${metrics}
    <div id="crmRetentionSegmentDrill"></div>
    <div class="pending-dashboard-panels">
      ${panel('Engagement outcomes', `${bar}${legend}`)}
      ${_loadByAgentPanel(panel)}
      ${funnelPanel}
      ${breakdownPanels}
    </div>`;
}

// Per-agent active-case load (#4): horizontal bars of open claimed cases per agent.
function _loadByAgentPanel(panel) {
  const items = (_loadByAgent && _loadByAgent.items) || [];
  if (!items.length) {
    return panel('Active load by agent', '<div class="muted" style="font-size:12px;">No claimed cases in progress.</div>');
  }
  const max = Math.max(...items.map((r) => r.count), 1);
  const rows = items.map((r) => {
    const pct = (r.count / max) * 100;
    return `<div style="display:flex; align-items:center; gap:8px; margin-bottom:4px; font-size:12px;">
      <div style="flex:0 0 120px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${escapeHtml(r.agent_name)}">${escapeHtml(r.agent_name)}</div>
      <div style="flex:1; background:var(--panel-2,#f1f5f9); border-radius:6px; overflow:hidden;"><div style="width:${pct}%; min-width:2px; height:14px; background:#6366f1;"></div></div>
      <div style="flex:0 0 auto; font-weight:700;">${r.count}</div>
    </div>`;
  }).join('');
  const total = _loadByAgent.total_cases != null ? _loadByAgent.total_cases : items.reduce((a, r) => a + r.count, 0);
  return panel(`Active load by agent · ${total} claimed`, rows);
}

const _SEGMENT_LABELS = {
  contacted: 'Contacted', recovered: 'Recovered', auto_recovered: 'Auto-recovered (self-resumed)',
  durable: 'Durable (still active)',
  resuspended: 'Re-suspended (revolving door)', ticketing: 'Still ticketing',
  active: 'Still active', moved_isp: 'Moved ISP', removed: 'Removed',
};

async function openReportSegmentDrill(segment) {
  const box = document.getElementById('crmRetentionSegmentDrill');
  if (!box) return;
  box.innerHTML = '<div class="muted" style="font-size:12px; padding-top:8px;">Loading…</div>';
  try {
    const data = await adminAPI.fetch(API.crmRetentionReportSegment(segment, null, _reportDays()), { method: 'GET', cache: 'no-store' });
    renderReportSegmentDrill(segment, data);
  } catch (err) {
    box.innerHTML = `<div class="error">${escapeHtml(adminAPI.formatErrorDetail(err) || 'Failed to load')}</div>`;
  }
}

function renderReportSegmentDrill(segment, data) {
  const box = document.getElementById('crmRetentionSegmentDrill');
  if (!box) return;
  const items = (data && data.items) || [];
  const esc = (v) => escapeHtml(v == null ? '' : String(v));
  const money = (v) => (v == null ? '—' : formatCurrency(v));
  const rows = items.slice(0, 500).map((c) => `<tr data-retention-goto="${esc(c.support_customer_id)}" style="cursor:pointer;" title="Open this case">
    <td>${esc(c.display_name)}</td><td>${esc(c.primary_phone)}</td>
    <td>${esc((c.account_state || '').toUpperCase())}</td>
    <td>${esc(String(c.status || '').replace(/_/g, ' '))}</td>
    <td style="text-align:right;">${money(c.overdue_balance)}</td>
    <td style="text-align:right;">${c.days_overdue == null ? '—' : c.days_overdue}</td>
    <td style="text-align:right;">${money(c.mrr)}</td></tr>`).join('');
  box.innerHTML = `
    <div style="display:flex; align-items:center; gap:10px; margin:8px 0 6px; flex-wrap:wrap; border-top:1px solid var(--border,#eee); padding-top:10px;">
      <strong style="font-size:13px;">${esc(_SEGMENT_LABELS[segment] || segment)} — ${data.count}</strong>
      <span class="spacer" style="flex:1;"></span>
      <button type="button" class="btn ghost" id="crmRetentionSegmentCsv" style="font-size:11px; padding:2px 8px;">⬇ Export CSV</button>
      <button type="button" class="btn ghost" data-retention-segment-close="1" style="font-size:11px; padding:2px 8px;">✕ Close</button>
    </div>
    <div class="table-wrap" style="max-height:340px; overflow:auto;">
      <table style="font-size:12px; width:100%;"><thead><tr>
        <th>Name</th><th>Phone</th><th>State</th><th>Case status</th><th>Overdue</th><th>Days od</th><th>MRR</th>
      </tr></thead><tbody>${rows || '<tr><td colspan="7" class="muted">No customers.</td></tr>'}</tbody></table>
    </div>
    ${items.length > 500 ? `<div class="muted" style="font-size:11px; margin-top:4px;">Showing first 500 of ${data.count}. Export CSV for the full list.</div>` : ''}`;
  const csvBtn = document.getElementById('crmRetentionSegmentCsv');
  if (csvBtn) {
    csvBtn.addEventListener('click', () => {
      const a = document.createElement('a');
      a.href = API.crmRetentionReportSegment(segment, 'csv', _reportDays());
      a.download = `retention_${segment}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    });
  }
}

function _breakdownRows(dim) {
  const rows = Object.entries(dim || {}).map(([val, counts]) => {
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    const recovered = counts.recovered || 0;
    return { val, total, recovered, rate: total ? Math.round((recovered / total) * 100) : 0 };
  }).sort((a, b) => b.total - a.total);
  return rows.map((r) =>
    `<div style="display:flex; gap:8px; font-size:12px; padding:1px 0;">
      <span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeHtml(r.val)}</span>
      <span class="muted">${r.total}</span>
      <span style="color:#10b981;">${r.recovered} (${r.rate}%)</span>
    </div>`).join('');
}

let _reportLoaded = false;
function _reportDays() {
  const sel = document.getElementById('crmRetentionReportDays');
  const d = sel ? parseInt(sel.value, 10) : 90;
  return Number.isFinite(d) && d > 0 ? d : 90;
}
async function loadReport() {
  const box = document.getElementById('crmRetentionReport');
  if (!box) return;
  const days = _reportDays();
  const win = document.getElementById('crmRetentionReportWindow');
  if (win) win.textContent = `Last ${days} days`;
  box.innerHTML = '<div class="muted">Loading report…</div>';
  try {
    const [rep, load] = await Promise.all([
      adminAPI.fetch(API.crmRetentionReport(`days=${days}`), { method: 'GET', cache: 'no-store' }),
      adminAPI.fetch(API.crmRetentionLoadByAgent(), { method: 'GET', cache: 'no-store' }).catch(() => null),
    ]);
    _loadByAgent = load;
    renderReport(rep);
    _reportLoaded = true;
  } catch (err) {
    box.innerHTML = `<div class="error">${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

function _reportOpen() {
  const d = document.getElementById('crmRetentionDashboard');
  return !!(d && d.open);
}

// ---- Churn analysis dashboard ----------------------------------------------
const CHURN_REASON_LABELS = {
  moved_isp: 'Moved to another ISP', terminated_no_move: 'Terminated (no move)',
  abandoned: 'Abandoned 12mo+', unknown: 'Unknown',
};
const CHURN_REASON_COLORS = {
  moved_isp: '#8b5cf6', terminated_no_move: '#ef4444', abandoned: '#f59e0b', unknown: '#9ca3af',
};
let _churnLoaded = false;

function _churnOpen() {
  const d = document.getElementById('crmChurnDashboard');
  return !!(d && d.open);
}
function _churnDays() {
  const sel = document.getElementById('crmChurnDays');
  const d = sel ? parseInt(sel.value, 10) : 90;
  return Number.isFinite(d) && d > 0 ? d : 90;
}

async function loadChurn() {
  const box = document.getElementById('crmChurnReport');
  if (!box) return;
  const days = _churnDays();
  const win = document.getElementById('crmChurnWindow');
  if (win) win.textContent = `Last ${days} days`;
  box.innerHTML = '<div class="muted">Loading churn analysis…</div>';
  try {
    const rep = await adminAPI.fetch(API.crmRetentionChurn(`days=${days}`), { method: 'GET', cache: 'no-store' });
    renderChurn(rep);
    _churnLoaded = true;
  } catch (err) {
    box.innerHTML = `<div class="error">${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

function renderChurn(rep) {
  const box = document.getElementById('crmChurnReport');
  if (!box) return;
  const byReason = rep.by_reason || {};
  const mrrByReason = rep.mrr_by_reason || {};
  const card = (label, value, color, sub) =>
    `<div class="pending-dashboard-card"><span class="label">${escapeHtml(label)}</span>`
    + `<span class="value"${color ? ` style="color:${color};"` : ''}>${value}</span>`
    + `${sub ? `<span class="sub">${sub}</span>` : ''}</div>`;
  const metrics = `<div class="pending-dashboard-metrics">
    ${card(`Churned (${rep.days}d)`, rep.total_churned, '#ef4444')}
    ${card('MRR lost / mo', _fmtMoney(rep.mrr_lost), '#8b5cf6')}
    ${card('Moved ISP', byReason.moved_isp || 0, CHURN_REASON_COLORS.moved_isp, _fmtMoney(mrrByReason.moved_isp))}
    ${card('Terminated (no move)', byReason.terminated_no_move || 0, CHURN_REASON_COLORS.terminated_no_move, _fmtMoney(mrrByReason.terminated_no_move))}
    ${card('Abandoned', byReason.abandoned || 0, CHURN_REASON_COLORS.abandoned, _fmtMoney(mrrByReason.abandoned))}
  </div>`;

  const panel = (title, inner) => `<div class="pending-dashboard-panel"><h3>${title}</h3><div>${inner}</div></div>`;

  // Reason split bar.
  const total = rep.total_churned || 0;
  let reasonBar = '<div class="muted" style="font-size:12px;">No terminations in the period.</div>';
  if (total) {
    const segs = Object.entries(byReason).filter(([, n]) => n > 0).map(([k, n]) => {
      const pct = (n / total) * 100;
      const c = CHURN_REASON_COLORS[k] || CHURN_REASON_COLORS.unknown;
      return `<div title="${escapeHtml(CHURN_REASON_LABELS[k] || k)}: ${n}" style="width:${pct}%; background:${c};"></div>`;
    }).join('');
    const legend = Object.entries(byReason).filter(([, n]) => n > 0).map(([k, n]) => {
      const c = CHURN_REASON_COLORS[k] || CHURN_REASON_COLORS.unknown;
      return `<span style="margin-right:12px;"><span style="display:inline-block; width:10px; height:10px; border-radius:2px; background:${c}; margin-right:4px;"></span>${escapeHtml(CHURN_REASON_LABELS[k] || k)} (${n})</span>`;
    }).join('');
    reasonBar = `<div style="display:flex; height:22px; border-radius:8px; overflow:hidden; background:var(--panel-2,#f1f5f9);">${segs}</div>`
      + `<div style="margin-top:6px; font-size:12px;">${legend}</div>`;
  }

  // Monthly trend.
  const monthly = rep.monthly || [];
  let trend = '<div class="muted" style="font-size:12px;">No data.</div>';
  if (monthly.length) {
    const max = Math.max(...monthly.map((m) => m.count), 1);
    trend = `<div style="display:flex; align-items:flex-end; gap:8px; height:90px;">${monthly.map((m) => {
      const h = Math.round((m.count / max) * 76) + 4;
      return `<div style="flex:1; display:flex; flex-direction:column; align-items:center; gap:2px;" title="${escapeHtml(m.month)}: ${m.count}">
        <div style="font-size:10px; color:var(--sub);">${m.count}</div>
        <div style="width:100%; max-width:34px; height:${h}px; background:#ef4444; border-radius:3px 3px 0 0;"></div>
        <div style="font-size:10px; color:var(--sub);">${escapeHtml(m.month.slice(2))}</div>
      </div>`;
    }).join('')}</div>`;
  }

  const bd = rep.breakdowns || {};
  const bdRows = (obj) => Object.entries(obj)
    .sort((a, b) => (b[1].count || 0) - (a[1].count || 0))
    .map(([k, v]) => `<div style="display:flex; justify-content:space-between; gap:10px; font-size:12px; padding:2px 0;">
      <span>${escapeHtml(k)}</span><span class="muted">${v.count} · ${_fmtMoney(v.mrr)}</span></div>`).join('') || '<span class="muted" style="font-size:12px;">—</span>';
  const breakdownPanels = ['class', 'region', 'plan']
    .filter((d) => bd[d] && Object.keys(bd[d]).length)
    .map((d) => panel(`By ${d} · count · MRR`, `<div class="crm-breakdown-scroll">${bdRows(bd[d])}</div>`))
    .join('');

  box.innerHTML = `${metrics}
    <div class="pending-dashboard-panels">
      ${panel('By reason', reasonBar)}
      ${panel('Monthly terminations', trend)}
      ${breakdownPanels}
    </div>`;
}

let _wired = false;
function wire() {
  if (_wired) return;
  if (!document.getElementById('crmRetentionView')) return;
  _wired = true;

  const sort = document.getElementById('crmRetentionSort');
  if (sort) sort.addEventListener('change', () => { _sort = sort.value || 'score'; _offset = 0; load(); });
  const statusSel = document.getElementById('crmRetentionStatusFilter');
  if (statusSel) statusSel.addEventListener('change', () => { _status = statusSel.value || ''; _offset = 0; load(); });
  const viewSel = document.getElementById('crmRetentionViewSelect');
  if (viewSel) viewSel.addEventListener('change', () => {
    _view = viewSel.value || 'active';
    if (sort) sort.disabled = _view !== 'active';   // sort only applies to the active worklist
    // The outcome filter also only applies to the active worklist.
    if (statusSel) statusSel.disabled = _view !== 'active';
    // Search is server-side for active/removed; at-risk + terminations aren't searchable.
    const si = document.getElementById('crmRetentionSearch');
    if (si) si.disabled = _view === 'at_risk' || _view === 'terminations';
    _offset = 0;
    load();
  });
  const searchInput = document.getElementById('crmRetentionSearch');
  const searchClear = document.getElementById('crmRetentionSearchClear');
  function applySearch() {
    const v = (searchInput && searchInput.value || '').trim();
    if (v === _search) return;
    _search = v;
    _offset = 0;
    if (searchClear) searchClear.style.display = v ? '' : 'none';
    load();
  }
  if (searchInput) {
    // Debounce typing; act immediately on Enter / clear.
    searchInput.addEventListener('input', () => {
      if (searchClear) searchClear.style.display = searchInput.value ? '' : 'none';
      clearTimeout(_searchTimer);
      _searchTimer = setTimeout(applySearch, 300);
    });
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { clearTimeout(_searchTimer); applySearch(); }
    });
  }
  if (searchClear) searchClear.addEventListener('click', () => {
    if (searchInput) searchInput.value = '';
    clearTimeout(_searchTimer);
    applySearch();
  });
  const refresh = document.getElementById('crmRetentionRefresh');
  if (refresh) refresh.addEventListener('click', () => { load(); if (_reportOpen()) loadReport(); });
  const prev = document.getElementById('crmRetentionPrev');
  if (prev) prev.addEventListener('click', () => _goToPage(-1));
  const next = document.getElementById('crmRetentionNext');
  if (next) next.addEventListener('click', () => _goToPage(1));
  // Expandable report dashboard at the top: lazy-load on first open.
  const dash = document.getElementById('crmRetentionDashboard');
  if (dash) dash.addEventListener('toggle', () => { if (dash.open && !_reportLoaded) loadReport(); });
  const reportDays = document.getElementById('crmRetentionReportDays');
  if (reportDays) reportDays.addEventListener('change', () => { _reportLoaded = false; if (_reportOpen()) loadReport(); });

  // Churn analysis dashboard: lazy-load on first open; re-load on period change.
  const churnDash = document.getElementById('crmChurnDashboard');
  if (churnDash) churnDash.addEventListener('toggle', () => { if (churnDash.open && !_churnLoaded) loadChurn(); });
  const churnDays = document.getElementById('crmChurnDays');
  if (churnDays) churnDays.addEventListener('change', () => { _churnLoaded = false; if (_churnOpen()) loadChurn(); });
  const churnExport = document.getElementById('crmChurnExport');
  if (churnExport) churnExport.addEventListener('click', () => {
    const a = document.createElement('a');
    a.href = API.crmRetentionChurnCustomers(`days=${_churnDays()}&format=csv`);
    a.download = 'churn.csv';
    document.body.appendChild(a); a.click(); a.remove();
  });

  // Tracking sidebar: a row's 📞 logs a call; clicking the row jumps to its card.
  const tracker = document.getElementById('crmRetentionTracker');
  if (tracker) tracker.addEventListener('click', (e) => {
    const act = e.target.closest('[data-track-action="outcome"]');
    if (act) { e.stopPropagation(); return void openOutcome(act.getAttribute('data-id'), act.getAttribute('data-name')); }
    const row = e.target.closest('[data-track-goto]');
    if (row) _gotoCase(row.getAttribute('data-track-goto'));
  });

  // Report KPI cards: click a card to drill into the customers behind it.
  const report = document.getElementById('crmRetentionReport');
  if (report) {
    const drill = (e) => {
      if (e.target.closest('[data-retention-segment-close]')) {
        const box = document.getElementById('crmRetentionSegmentDrill');
        if (box) box.innerHTML = '';
        return;
      }
      const row = e.target.closest('[data-retention-goto]');
      if (row) return void _gotoCase(row.getAttribute('data-retention-goto'));
      const seg = e.target.closest('[data-segment]');
      if (seg) openReportSegmentDrill(seg.getAttribute('data-segment'));
    };
    report.addEventListener('click', drill);
    report.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('[data-segment]')) {
        e.preventDefault();
        drill(e);
      }
    });
  }

  const list = document.getElementById('crmRetentionList');
  if (list) {
    list.addEventListener('click', (e) => {
      const term = e.target.closest('[data-termination-action]');
      if (term) {
        const tid = term.getAttribute('data-id');
        const tname = term.getAttribute('data-name');
        if (term.getAttribute('data-termination-action') === 'terminate') return void terminateCase(tid, tname);
        if (term.getAttribute('data-termination-action') === 'reverse') return void reverseTermination(tid, tname);
        return;
      }
      const btn = e.target.closest('[data-retention-action]');
      if (!btn) return;
      const action = btn.getAttribute('data-retention-action');
      const id = btn.getAttribute('data-id');
      if (action === 'expand') return void toggleDossier(id, btn);
      if (action === 'score') return void scoreCase(id);
      if (action === 'outcome') return void openOutcome(id, btn.getAttribute('data-name'));
      if (action === 'remove') return void removeCase(id, btn.getAttribute('data-name'));
      if (action === 'claim') return void claimCase(id, false);
      if (action === 'release') return void claimCase(id, true);
    });
  }
  const saveBtn = document.getElementById('crmRetentionOutcomeSave');
  if (saveBtn) saveBtn.addEventListener('click', () => saveOutcome());
  const outcomeSel = document.getElementById('crmRetentionOutcomeSelect');
  if (outcomeSel) outcomeSel.addEventListener('change', _syncOutcomeReasonVisibility);
}

function mount() {
  wire();
  // The Terminations view is supervisor/root only — drop the option otherwise.
  const viewSel = document.getElementById('crmRetentionViewSelect');
  const termOpt = viewSel && viewSel.querySelector('option[value="terminations"]');
  if (termOpt && !_canTerminate()) termOpt.remove();
  _offset = 0;
  load();
}

export default { mount, load };
